import React from 'react';
import { Button } from '@/components/ui/button.tsx';
import { PlusIcon } from 'lucide-react';
import { ExpandedState, Row, RowData } from '@tanstack/react-table';
import {
  createDefaultDevice,
  TDevice,
  TTreeDevice,
} from '@/features/devices/schemas/devices.schemas.ts';
import { useTranslation } from 'react-i18next';

export const DeviceTableToolbarAddButton = ({
  data,
  deviceInfo,
  getSelectedTableRow,
  setData,
  setExpandedRows,
}: {
  data: TTreeDevice[];
  deviceInfo: {
    companyId: string;
    plantId: string;
    timezone: string;
  };
  getSelectedTableRow: () => Row<RowData> | undefined;
  setData: (devices: TTreeDevice[]) => void;
  setExpandedRows: React.Dispatch<React.SetStateAction<ExpandedState>>;
}) => {
  const createNewDevice = (): TDevice => {
    const newSN = `NEW-${Date.now()}`;

    return {
      ...createDefaultDevice(deviceInfo),
      name: newSN,
      SN: newSN,
    };
  };

  const handleAddDevice = () => {
    const selectedRow = getSelectedTableRow();
    const newDevice = createNewDevice();

    if (!selectedRow) {
      setData([...data, newDevice]);
      return;
    }

    const addDeviceToNode = (devices: TTreeDevice[], targetPath: number[]): TTreeDevice[] => {
      if (targetPath.length === 0) {
        return devices;
      }

      return devices.map((device, index) => {
        if (index === targetPath[0]) {
          if (targetPath.length === 1) {
            return {
              ...device,
              subDevices: device.subDevices ? [...device.subDevices, newDevice] : [newDevice],
            };
          }
          return {
            ...device,
            subDevices: device.subDevices
              ? addDeviceToNode(device.subDevices, targetPath.slice(1))
              : device.subDevices,
          };
        }
        return device;
      });
    };

    const pathIndices = selectedRow.id.split('.').map(Number);

    const newData = addDeviceToNode(data, pathIndices);
    setData(newData);

    setExpandedRows((prev) => {
      const newExpanded: Record<string, boolean> = typeof prev === 'object' ? { ...prev } : {};
      newExpanded[selectedRow.id] = true;
      return newExpanded;
    });
  };
  const { t } = useTranslation();

  return (
    <Button
      type="button"
      onClick={handleAddDevice}
      variant="ghost"
      className="flex items-center gap-2 rounded-none py-5 font-normal hover:bg-gray-200"
    >
      <PlusIcon className="text-muted-foreground size-4" />
      <span>{t('common.buttons.addButton')}</span>
    </Button>
  );
};
